import React, { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Filter, RotateCcw } from "lucide-react";

const ALL = "all";

export function DashboardFilters({
  user, agencies, categories, counters, agents,
  value, onChange, onReset,
}) {
  const showAgencyFilter = user?.role === "SUPER_ADMIN" || user?.role === "AUDITOR";

  const filteredCounters = useMemo(() => {
    if (!value.agency_id || value.agency_id === ALL) return counters;
    return counters.filter((c) => c.agency_id === value.agency_id);
  }, [counters, value.agency_id]);

  const set = (k, v) => onChange({ ...value, [k]: v === ALL ? "" : v });

  return (
    <Card className="border-slate-200 shadow-none" data-testid="dashboard-filters">
      <CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {showAgencyFilter && (
          <Field label="Agence">
            <Select value={value.agency_id || ALL} onValueChange={(v) => set("agency_id", v)}>
              <SelectTrigger data-testid="filter-agency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toutes les agences</SelectItem>
                {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        )}
        <Field label="Catégorie">
          <Select value={value.category_code || ALL} onValueChange={(v) => set("category_code", v)}>
            <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.code}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Guichet">
          <Select value={value.counter_id || ALL} onValueChange={(v) => set("counter_id", v)}>
            <SelectTrigger data-testid="filter-counter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous</SelectItem>
              {filteredCounters.map((c) => <SelectItem key={c.id} value={c.id}>{c.counter_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Agent">
          <Select value={value.agent_id || ALL} onValueChange={(v) => set("agent_id", v)}>
            <SelectTrigger data-testid="filter-agent"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous</SelectItem>
              {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Date début">
          <Input type="date" value={value.date_start || ""}
                 onChange={(e) => set("date_start", e.target.value)}
                 data-testid="filter-date-start" />
        </Field>
        <Field label="Date fin">
          <Input type="date" value={value.date_end || ""}
                 onChange={(e) => set("date_end", e.target.value)}
                 data-testid="filter-date-end" />
        </Field>

        <div className="col-span-2 md:col-span-3 lg:col-span-6 flex items-center justify-between pt-2">
          <div className="text-xs text-slate-500 inline-flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5" />
            Les filtres s'appliquent à toutes les statistiques ci-dessous.
          </div>
          <Button variant="outline" size="sm" onClick={onReset} data-testid="filter-reset">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Réinitialiser
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest text-slate-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
